<template>
  <div class="home-page pt-5">
    <!-- 1. Hero + barra de beneficios superpuesta + franja de puntos -->
    <div class="home-hero-features-wrap py-0">
      <HeroSection data-aos="fade" data-aos-duration="700" />
      <div class="home-features-below-hero">
        <div class="home-features-below-hero__panel" data-aos="fade" data-aos-delay="120">
          <HomeFeaturesBar floating data-aos="fade" data-aos-delay="160" />
        </div>
      </div>
    </div>

    <!-- 1c. Banners de categorías principales + carrusel de ofertas -->
    <HomeCategoryQuickTiles data-aos="fade" data-aos-delay="120" />
    <section v-if="onSaleProductsLimited.length" class="home-dark-band">
      <div class="container px-2 px-lg-4">
        <div class="home-products-carousel home-products-carousel--sale">
          <div class="home-products-carousel__head">
            <h2 class="text-white">¡Ofertas imperdibles!</h2>
          </div>
          <ProductsCarousel
          :products="onSaleProductsLimited"
          :is-loading="loadingOnSale"
          :items-per-slide="4"
          />
        </div>
      </div>
    </section>
    <HomeRepairServiceBanner data-aos="fade" data-aos-delay="120" />
    <section class="home-dark-band">
      <div class="container px-2 px-lg-4">
        <div class="home-products-carousel">
          <div class="home-products-carousel__head">
            <h2 class="text-white">Últimos agregados</h2>
          </div>
          <ProductsCarousel
            :products="latestProductsLimited"
            :is-loading="loadingLatest"
            :items-per-slide="4"
          />
        </div>
      </div>
    </section>



    <section class="home-opinions-brands mt-0 pt-0 home-dark-band">
      <HomeTestimonialsSection />
    </section>
  </div>
</template>

<script setup>
import { ref, onMounted, computed, nextTick, watch } from 'vue'
import { useStore } from 'vuex'

// New home components
import HeroSection from '@/components/home/HeroSection.vue'
import HomeFeaturesBar from '@/components/home/HomeFeaturesBar.vue'
import HomeCategoryQuickTiles from '@/components/home/HomeCategoryQuickTiles.vue'
import ProductsCarousel from '@/components/ProductsCarousel.vue'
import HomeRepairServiceBanner from '@/components/home/HomeRepairServiceBanner.vue'
import HomeTestimonialsSection from '@/components/home/HomeTestimonialsSection.vue'
// Composables
import { useScrollReveal } from '@/composables/useScrollReveal'
import AOS from 'aos'
import 'aos/dist/aos.css'

// Services
import { getBlogPosts } from '@/services/blog'
import { getCategories } from '@/services/categories'


// Vuex store
const store = useStore()

// Scroll reveal
const { observeAll } = useScrollReveal()

const refreshHomeAnimations = async () => {
  await nextTick()
  observeAll('.reveal')
  observeAll('.reveal-left')
  observeAll('.reveal-right')
  observeAll('.reveal-scale')
  if (typeof AOS.refreshHard === 'function') {
    AOS.refreshHard()
  }
}

// ----- Categories -----
const categories = ref([])

// ----- Products from Vuex -----
const onSaleProducts = computed(() => store.getters['products/onSaleProducts'])
const latestProducts = computed(() => store.getters['products/regularProducts'])
const loadingOnSale = computed(() => store.state.products.loadingOnSale)
const loadingLatest = computed(() => store.state.products.loading)

const latestProductsLimited = computed(() => latestProducts.value.slice(0, 12))
const onSaleProductsLimited = computed(() => onSaleProducts.value.slice(0, 12))


// ----- Blog -----
const latestBlogs = ref([])
const loadingBlogs = ref(true)

// ----- Lifecycle -----
onMounted(async () => {
  try {
    // 1. Preload on-sale products
    store.dispatch('products/preloadProductsOnSale')

    // 2. Fetch on-sale products (el skeleton del carrusel cubre la espera)
    store.dispatch('products/fetchProductsOnSale').catch(e => {
      console.error('Error loading products on sale:', e)
    })

    // 3. Fetch categories
    getCategories()
      .then(data => {
        if (Array.isArray(data)) {
          categories.value = data
        } else if (data?.results) {
          categories.value = data.results
        }
      })
      .catch(e => console.error('Error loading categories:', e))

    // 4. Fetch latest products (slight delay for priority)
    setTimeout(() => {
      store.dispatch('products/fetchProducts').catch(e => {
        console.error('Error loading latest products:', e)
      })
    }, 300)

    // 5. Blogs
    loadingBlogs.value = true
    getBlogPosts({ status: 'published', page_size: 3 })
      .then(data => {
        latestBlogs.value = data.results || []
      })
      .catch(e => {
        console.error('Error loading blog posts:', e)
        latestBlogs.value = []
      })
      .finally(() => {
        loadingBlogs.value = false
      })
  } catch (error) {
    console.error('Error initializing home:', error)
  }

  // Liberar el loader apenas la página pintó su estado de carga (skeletons).
  // No esperamos a que terminen las APIs: eso lo cubren los skeletons y el
  // min-height del layout (el footer no sube aunque el contenido sea corto).
  await nextTick()
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('app:initial-data-ready'))
  }

  // Initialize AOS and scroll reveal after DOM settles
  nextTick(() => {
    setTimeout(() => {
      AOS.init({
        once: true,
        offset: 56,
        duration: 820,
        easing: 'ease-out-cubic',
        anchorPlacement: 'top-bottom',
        disableMutationObserver: false,
        disable: () =>
          typeof window !== 'undefined' &&
          window.matchMedia &&
          window.matchMedia('(prefers-reduced-motion: reduce)').matches,
      })
      refreshHomeAnimations()
    }, 100)
  })
})

watch(
  [
    () => loadingLatest.value,
    () => latestProducts.value.length,
    () => loadingOnSale.value,
    () => onSaleProducts.value.length
  ],
  () => {
    refreshHomeAnimations()
  }
)
</script>

<script>
export default {
  name: 'AppHome'
}
</script>

<style scoped>
.services-video-teaser-root {
  display: block;
  height: 100%;
  min-height: 100%;
}

.services-section__video {
  max-width: 100%;
  height: 800px;
  overflow: hidden;
}

/* ========================================
   HOME PAGE LAYOUT
   ======================================== */
.home-page {
  /* Fondo oscuro: evita destello blanco al animar secciones (AOS) sobre --bg-primary */
  background-color: #000;
  min-height: 100vh;
  overflow-x: clip;
}

.home-dark-band {
  background: #000;
  padding: clamp(2rem, 5vw, 3.5rem) 0;
}

.home-page > * + * {
  margin-top: clamp(2.5rem, 6vw, 4.5rem);
}

.home-hero-features-wrap {
  padding-bottom: 0;
}


.home-products-carousel {
  padding: 0;
}

.home-products-carousel__head {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  margin-bottom: 1.25rem;
  text-align: center;
}

.home-products-carousel__head h2 {
  margin: 0;
  font-size: clamp(2.2rem, 4.2vw, 3rem);
  font-weight: 500;
}

.home-products-carousel--sale {
  padding-top: 0;
}


.home-page :deep(h1),
.home-page :deep(h2),
.home-page :deep(h3),
.home-page :deep(h4) {
  font-family: 'Anton', sans-serif;
  text-transform: uppercase;
  letter-spacing: 0.01em;
  font-weight: 500;
}

/* ========================================
   ABOUT VIDEO TEASER
   ======================================== */
.home-about-teaser {
  padding: clamp(2.5rem, 6vw, 4.5rem) 0;
}

.home-about-teaser__media {
  overflow: hidden;
  box-shadow: 0 18px 40px rgba(0, 0, 0, 0.45);
  height: 100%;
}

.home-about-teaser__media :deep(.service-video-teaser) {
  min-height: clamp(22rem, 56vw, 34rem);
  height: 100%;
}

.home-about-teaser__content {
  color: #f5f5f5;
  background: #0b0b0b;
  padding: clamp(1.25rem, 3vw, 2rem);
  box-shadow: 0 14px 34px rgba(0, 0, 0, 0.18);
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.home-about-teaser__row {
  --about-radius: clamp(1rem, 2vw, 1.5rem);
}

@media (min-width: 992px) {
  .home-about-teaser__media {
    border-radius: var(--about-radius) 0 0 var(--about-radius);
  }

  .home-about-teaser__content {
    border-radius: 0 var(--about-radius) var(--about-radius) 0;
  }
}

@media (max-width: 991.98px) {
  .home-about-teaser__media {
    border-radius: var(--about-radius) var(--about-radius) 0 0;
  }

  .home-about-teaser__content {
    border-radius: 0 0 var(--about-radius) var(--about-radius);
  }
}

.home-about-teaser__eyebrow {
  text-transform: uppercase;
  letter-spacing: 0.2em;
  font-size: 0.75rem;
  font-weight: 700;
  color: rgba(255, 255, 255, 0.6);
  margin-bottom: 0.75rem;
}

.home-about-teaser__content h2 {
  font-size: clamp(1.8rem, 3.8vw, 2.6rem);
  font-weight: 600;
  letter-spacing: -0.02em;
  margin-bottom: 1rem;
}

.home-about-teaser__content p {
  color: rgba(255, 255, 255, 0.8);
  font-size: 1rem;
  line-height: 1.7;
  margin-bottom: 0.9rem;
}

/* Hero + barra de beneficios superpuesta (franja debajo del banner: fondo plano, sin puntos) */
.home-hero-features-wrap {
  position: relative;
  --home-hero-surface: #000;
}

.home-features-below-hero__dots {
  height: clamp(150px, 20vw, 220px);
  position: relative;
  z-index: 0;
  background-color: var(--home-hero-surface, #000);
}

.home-features-below-hero__panel {
  position: relative;
  margin-top: -200px;
  z-index: 2;
  pointer-events: none;
  padding-left: max(0.5rem, env(safe-area-inset-left, 0px));
  padding-right: max(0.5rem, env(safe-area-inset-right, 0px));
}

@media (max-width: 1400px) {
  .home-features-below-hero__panel {
    margin-top: -70px;
  }
}


@media (max-width: 575.98px) {
  .home-features-below-hero__panel {
    padding-left: max(0px, env(safe-area-inset-left, 0px));
    padding-right: max(0px, env(safe-area-inset-right, 0px));
  }

  .home-features-below-hero__dots {
    height: clamp(5.5rem, 24vw, 7.5rem);
  }

}

.home-features-below-hero__panel :deep(.home-features-bar) {
  pointer-events: auto;
  padding-top: 0 !important;
}

/* ========================================
   CATEGORIES GRID
   ======================================== */
.services-section__content {
  height: 100%;
}

.services-section__content h2 {
  font-size: 4rem;
  font-weight: 600;
  line-height: 1.15;
  letter-spacing: -0.02em;
  margin-bottom: var(--space-lg);
}

.services-section__content {
  height: 800px;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

@media (max-width: 991.98px) {
  .services-section__video {
    height: auto;
    min-height: clamp(18rem, 78vw, 30rem);
    aspect-ratio: 4 / 5;
    max-height: none;
  }

  .services-section__content img {
    display: none;
  }

  .services-section__content {
    height: auto;
  }
}

.btn-primary {
  max-width: 250px;
}

.category-tile-tech {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  min-height: 140px;
  padding: var(--space-lg);
  border-radius: var(--radius-md);
  background: linear-gradient(145deg, #141414 0%, #0a0a0a 100%);
  border: 1px solid rgba(220, 38, 38, 0.35);
  text-decoration: none;
  color: #fff;
  transition: transform var(--transition-base), border-color var(--transition-base), box-shadow var(--transition-base);
}

.category-tile-tech:hover {
  transform: translateY(-3px);
  border-color: var(--primary-color);
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.35);
  color: #fff;
}

.category-tile-tech--all {
  background: linear-gradient(145deg, var(--primary-dark) 0%, #0a0a0a 100%);
  border-color: rgba(255, 255, 255, 0.2);
}

.category-tile-tech__label {
  font-weight: 800;
  font-size: 1.1rem;
  letter-spacing: -0.02em;
  line-height: 1.2;
  margin-bottom: 0.35rem;
}

.category-tile-tech__hint {
  font-size: 0.8rem;
  color: rgba(255, 255, 255, 0.55);
  text-transform: uppercase;
  letter-spacing: 0.06em;
}

.category-tile-tech:hover .category-tile-tech__hint {
  color: rgba(255, 255, 255, 0.85);
}

@media (max-width: 575.98px) {
  .category-tile-tech {
    min-height: 120px;
    padding: var(--space-md);
  }
}

/* ========================================
   DISCOUNTS SECTION
   ======================================== */
.discounts-section {
  background-color: var(--bg-tertiary);
}

.discounts-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: var(--space-lg);
}

@media (max-width: 991.98px) {
  .discounts-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 575.98px) {
  .discounts-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: var(--space-md);
  }
}

.offers {
  display: flex;
  flex-wrap: wrap;
  align-items: stretch;
}

.offers__image-wrapper {
  background: #0b0b0b;
  color: #fff;
  height: 700px;
  width: 50%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
}

.offers__carousel-wrapper {
  width: 50%;
  height: 700px;
  overflow: hidden;
  flex-shrink: 0;
}

@media (max-width: 991.98px) {
  .offers__image-wrapper {
    width: 100%;
    height: 420px;
  }

  .offers__carousel-wrapper {
    width: 100%;
    height: 400px;
  }
}

@media (max-width: 575.98px) {
  .offers__image-wrapper {
    height: 360px;
  }

  .offers__carousel-wrapper {
    height: 340px;
  }
}

/* ========================================
   BLOG SECTION
   ======================================== */

/* Blog skeletons */

.blog-card-wrapper {
  max-width: 600px;
  margin: 0 auto;
}

.blog-skeletons {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: var(--space-xl);
}

.blog-skeleton-card {
  background: var(--bg-primary);
  border-radius: var(--radius-lg);
  overflow: hidden;
  box-shadow: var(--shadow-sm);
}

.blog-skeleton-card .skeleton-img {
  height: 200px;
  background: linear-gradient(90deg, var(--gray-100) 25%, var(--gray-200) 50%, var(--gray-100) 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
}

.blog-skeleton-card .skeleton-body {
  padding: var(--space-lg);
}

.skeleton-line {
  height: 14px;
  background: linear-gradient(90deg, var(--gray-100) 25%, var(--gray-200) 50%, var(--gray-100) 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 4px;
  margin-bottom: var(--space-sm);
}

.skeleton-line.w-40 {
  width: 40%;
}

.skeleton-line.w-60 {
  width: 60%;
}

.skeleton-line.w-80 {
  width: 80%;
}

@keyframes shimmer {
  0% {
    background-position: -200% 0;
  }

  100% {
    background-position: 200% 0;
  }
}

@media (max-width: 991.98px) {
  .blog-skeletons {
    grid-template-columns: 1fr;
    max-width: 400px;
    margin: 0 auto;
  }
}
</style>
